import { useContext, useState, useEffect, useRef, useCallback } from "react";
import "./login.scss";
import {
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import logo from "./final-logo-white.png";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const AUTO_LOGIN_KEY = "sb_auto_login";
const SAVED_EMAIL_KEY = "sb_saved_email";
const SAVED_PASSWORD_KEY = "sb_saved_password";
const SHORT_INTERVAL = 3 * 60; // 3 minutes in seconds
const LONG_INTERVAL = 60 * 60; // 1 hour in seconds
const BACKOFF_THRESHOLD = 10;

const Login = () => {
  const [error, setError] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [autoLogin, setAutoLogin] = useState(localStorage.getItem(AUTO_LOGIN_KEY) === "true");
  const [countdown, setCountdown] = useState(0);
  const [attempting, setAttempting] = useState(false);
  const failCountRef = useRef(0);
  const countdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  const hasSavedCredentials = () => {
    return !!(localStorage.getItem(SAVED_EMAIL_KEY) && localStorage.getItem(SAVED_PASSWORD_KEY));
  };

  const performLogin = useCallback((loginEmail, loginPassword) => {
    return signInWithEmailAndPassword(auth, loginEmail, loginPassword)
      .then((userCredential) => {
        const userQuery = query(
          collection(db, "users"),
          where("email", "==", loginEmail)
        );

        return getDocs(userQuery).then((querySnapshot) => {
          if (querySnapshot.empty) {
            throw new Error("User account not found. Please contact support.");
          }

          const userData = querySnapshot.docs[0].data();

          if (!userData.stationboard) {
            throw new Error("You do not have permission to access Stationboard");
          }

          return { userCredential, userData };
        });
      })
      .then((result) => {
        if (!result) return;

        // Save credentials for auto-login
        localStorage.setItem(SAVED_EMAIL_KEY, loginEmail);
        localStorage.setItem(SAVED_PASSWORD_KEY, btoa(loginPassword));

        dispatch({ type: "LOGIN", payload: result.userCredential.user });
        navigate("/");
      });
  }, [dispatch, navigate]);

  const startCountdown = useCallback((seconds) => {
    setCountdown(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const attemptAutoLogin = useCallback(() => {
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    const savedPassword = localStorage.getItem(SAVED_PASSWORD_KEY);
    if (!savedEmail || !savedPassword) return;

    setAttempting(true);
    setError(false);
    performLogin(savedEmail, atob(savedPassword))
      .then(() => {
        failCountRef.current = 0;
      })
      .catch(() => {
        failCountRef.current += 1;
        setAttempting(false);
        const interval = failCountRef.current >= BACKOFF_THRESHOLD ? LONG_INTERVAL : SHORT_INTERVAL;
        startCountdown(interval);
        timeoutRef.current = setTimeout(() => {
          attemptAutoLogin();
        }, interval * 1000);
      });
  }, [performLogin, startCountdown]);

  // Pre-fill form with saved credentials
  useEffect(() => {
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    const savedPassword = localStorage.getItem(SAVED_PASSWORD_KEY);
    if (savedEmail) setEmail(savedEmail);
    if (savedPassword) setPassword(atob(savedPassword));
  }, []);

  // Auto-login effect
  useEffect(() => {
    if (!autoLogin || !hasSavedCredentials()) return;

    // Wait 30 seconds before first attempt to give user time to change credentials
    startCountdown(30);
    timeoutRef.current = setTimeout(() => {
      attemptAutoLogin();
    }, 30 * 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [autoLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoLoginToggle = (enabled) => {
    setAutoLogin(enabled);
    localStorage.setItem(AUTO_LOGIN_KEY, enabled);

    if (!enabled) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setCountdown(0);
      setAttempting(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError(false);

    performLogin(email, password)
      .catch((error) => {
        console.error("Login error:", error.code || "unknown error");

        if (
          error.code === "auth/wrong-password" ||
          error.code === "auth/user-not-found"
        ) {
          setError(new Error("Username or password is incorrect"));
        } else if (error.code === "auth/too-many-requests") {
          setError(
            new Error(
              "Too many failed login attempts. Please try again later or reset your password."
            )
          );
        } else {
          setError(error);
        }
      });
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0
      ? `${mins}:${secs.toString().padStart(2, "0")}`
      : `${secs}s`;
  };

  return (
    <div className="login-page">
      <div className="img-container">
        <div className="img-text"></div>
      </div>
      <div className="login-container">
        <img src={logo} alt="Logo" className="login-logo" />
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="login-input"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
          />
          <div className="auto-login-row">
            <div className="auto-login-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={autoLogin}
                  onChange={(e) => handleAutoLoginToggle(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">Auto Login</span>
            </div>
            <div className="auto-login-status">
              {autoLogin && !hasSavedCredentials() && (
                <span className="caution-message">Please enter credentials</span>
              )}
              {autoLogin && hasSavedCredentials() && attempting && (
                <span className="attempting-message">Connecting...</span>
              )}
              {autoLogin && hasSavedCredentials() && !attempting && countdown > 0 && (
                <span className="countdown-message">Retry in {formatCountdown(countdown)}</span>
              )}
            </div>
          </div>
          <button type="submit" className="login-button">
            LOGIN
          </button>
          {error && (
            <span className="login-error">
              {error.message}
            </span>
          )}
        </form>
        <div className="warning">
          <span>* * * * * * * * W A R N I N G * * * * * * * *</span>
          <p>
            This system is the property of Ladder Suite LLC and is provided for
            official use by authorized government personnel and affiliated
            agencies only. Unauthorized access, use, or modification of this
            system is strictly prohibited and may result in disciplinary action,
            civil penalties, and/or criminal prosecution under applicable laws.
          </p>
          <p>
            All activity on this system is subject to monitoring, recording, and
            auditing. By proceeding, you expressly consent to such monitoring
            and acknowledge your obligation to comply with all applicable
            policies, including Ladder Suite's Terms of Service and any
            governing agency regulations.
          </p>
          <p>
            If you are not an authorized user or do not agree to these
            conditions, <strong>log off immediately</strong>.
          </p>
          <br />
        </div>
      </div>
    </div>
  );
};

export default Login;
